import { FolderSimplePlus } from '@phosphor-icons/react';
import { useId } from '@react-aria/utils';
import { ComponentProps } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import { FileOrFolder } from '../../TabDisplay';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { InputField } from '~/components/ui/input';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { useCurrentUser } from '~/services/api/users/endpoints';
import { useForm } from '~/utils/forms';

export default function CreateFolderModal({
  reportId,
  folderId,
  currentDirectoryItems,
}: {
  reportId?: string | undefined;
  folderId: number | null | undefined;
  currentDirectoryItems: FileOrFolder[];
}) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const query = useCurrentUser();

  const createFolder = useWrappedMutation(
    ReportService.endpoints.createFolderCasemanager.useMutation(),
  );

  const onSubmitHandler = async (
    data: Parameters<
      NonNullable<ComponentProps<typeof CreateFolderForm>['onSubmit']>
    >[0],
  ) => {
    const folderNames = currentDirectoryItems
      .filter((item) => item.is_folder === true)
      .map((folder) => folder.name);
    const isDuplicate = folderNames.includes(data.folderName.trim());
    if (isDuplicate) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Folder name already exists in the current level.',
        }),
        'error',
      );
      return;
    }
    await createFolder.mutateAsync(
      {
        name: data.folderName,
        uploaderID: data.userId,
        report: data.reportId,
        parent_folder: data.folderId,
      },
      {
        onSuccess() {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Folder created.',
            }),
            'success',
          );
        },
        onError(error) {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  };

  const isAllowedToCreateFolder = !!reportId && !!folderId;
  const isSubmitting = createFolder.state.isLoading;
  const formId = useId();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant='outlined'
          color='neutral'
          startDecorator={<FolderSimplePlus />}
          disabled={!isAllowedToCreateFolder}
        >
          <FormattedMessage defaultMessage='Create folder' />
        </Button>
      </DialogTrigger>
      <DialogContent
        onInteractOutside={(e) => !isSubmitting && e.preventDefault()}
      >
        <DialogTitle>
          <FormattedMessage defaultMessage='New Folder' />
        </DialogTitle>
        <DialogBody>
          <QueryResolver query={query}>
            {(user) => (
              <CreateFolderForm
                id={formId}
                defaultValues={{
                  reportId,
                  folderId: folderId ?? undefined,
                  userId: user.id,
                  folderName: '',
                }}
                onSubmit={onSubmitHandler}
              />
            )}
          </QueryResolver>
        </DialogBody>

        <DialogActions>
          <DialogClose disabled={isSubmitting} asChild>
            <Button variant='outlined' color='neutral'>
              Cancel
            </Button>
          </DialogClose>

          <Button
            form={formId}
            color='secondary'
            type='submit'
            loading={isSubmitting}
          >
            {intl.formatMessage({ defaultMessage: 'Create New Folder' })}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

const schema = z.object({
  folderName: z.string().min(1, 'Folder name is required'),
  reportId: z.string(),
  folderId: z.number(),
  userId: z.number(),
});

function CreateFolderForm({
  id,
  defaultValues,
  onSubmit: onSubmitFormHandler,
}: {
  id?: string;
  defaultValues?: Partial<z.infer<typeof schema>>;
  onSubmit?: (data: z.infer<typeof schema>) => PromiseLike<void>;
}) {
  const formId = useId(id);
  const intl = useIntl();
  const form = useForm({
    schema,
    defaultValues,
  });
  const onSubmitHandler = form.handleSubmit(
    (data) => !!onSubmitFormHandler && onSubmitFormHandler(data),
  );

  return (
    <form id={formId} onSubmit={onSubmitHandler}>
      <InputField
        control={form.control}
        field='folderName'
        aria-label={intl.formatMessage({ defaultMessage: 'Folder Name' })}
        placeholder='Folder Name'
      />
    </form>
  );
}
